import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOperationalSettings } from '@/hooks/useSettings';
import { Plus, Trash2, User, Calendar, Clock, AlertTriangle, UserCheck, Eye, List, LayoutGrid, Paperclip, FileText, ChevronRight, ChevronDown, Pencil, FolderPlus, ThumbsUp, Hourglass, Camera, ClipboardList, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { ProjectScope } from '@/lib/schemas';
import { useFirmType } from '@/hooks/useFirmType';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { InlineEditActions } from '@/components/ui/InlineEditActions';
import { DeliverableStatusBadge } from './DeliverableStatusBadge';
import { DeliverableFileList } from './DeliverableFileList';
import { DeliverableDocumentList } from './DeliverableDocumentList';
import { SubmissionGroup } from './SubmissionGroup';
import {
  useDeliverables,
  useCreateDeliverable,
  useUpdateDeliverable,
  useDeleteDeliverable,
} from '@/hooks/useDeliverables';
import {
  useSubmissions,
  useCreateSubmission,
  useUpdateSubmission,
  useDeleteSubmission,
} from '@/hooks/useSubmissions';
import { useProjectScopes } from '@/hooks/useProjectScopes';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import { useSiteVisits, type SiteVisit } from '@/hooks/useSiteVisits';
import type { Deliverable, DeliverableStatus, DeliverableUpdate } from '@/hooks/useDeliverables';
import type { TeamMember } from '@/hooks/useTeamMembers';
import type { SubmissionType } from '@/hooks/useSubmissions';

export const ALL_STATUSES: DeliverableStatus[] = [
  'pending',
  'in_progress',
  'in_review',
  'revision',
  'approved',
  'delivered',
];

export const STATUS_LABELS: Record<DeliverableStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  in_review: 'In Review',
  revision: 'Revision',
  approved: 'Approved',
  delivered: 'Delivered',
};

interface DeliverablesSectionProps {
  projectId: string;
}

function timeAgo(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffDays > 0) return `${diffDays}d`;
  if (diffHours > 0) return `${diffHours}h`;
  return 'just now';
}

function getBallHolder(deliverable: Deliverable): { name: string; role: string } | null {
  switch (deliverable.status) {
    case 'in_progress':
      return deliverable.assigned_to
        ? { name: deliverable.assigned_to, role: 'working' }
        : null;
    case 'in_review':
      return deliverable.reviewer
        ? { name: deliverable.reviewer, role: 'reviewing' }
        : deliverable.assigned_to
          ? { name: deliverable.assigned_to, role: 'reviewing' }
          : null;
    case 'revision':
      return deliverable.assigned_to
        ? { name: deliverable.assigned_to, role: 'revising' }
        : null;
    case 'approved':
      return { name: 'PM', role: 'to deliver' };
    default:
      return null;
  }
}

function isStale(deliverable: Deliverable, staleDays: number = 3): boolean {
  if (!['in_review', 'revision'].includes(deliverable.status)) return false;
  if (!deliverable.status_changed_at) return false;
  const now = new Date();
  const changed = new Date(deliverable.status_changed_at);
  const diffDays = (now.getTime() - changed.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays >= staleDays;
}

function isOverdue(deliverable: Deliverable): boolean {
  if (!deliverable.due_date) return false;
  if (deliverable.status === 'delivered' || deliverable.status === 'approved') return false;
  return new Date(deliverable.due_date + 'T00:00:00') < new Date();
}

/** Return acceptance status: 'accepted' | 'overdue' | 'pending' | null (if no tracking) */
function getDeliverableAcceptanceStatus(deliverable: Deliverable, overdueHours: number): 'accepted' | 'overdue' | 'pending' | null {
  // V118 adds accepted_at — undefined means old data before migration
  if (deliverable.accepted_at === undefined) return null;
  if (deliverable.accepted_at) return 'accepted';
  // Overdue = created > overdueHours ago and not yet accepted
  if (deliverable.created_at) {
    const ageHours = (Date.now() - new Date(deliverable.created_at).getTime()) / 3_600_000;
    if (ageHours > overdueHours) return 'overdue';
  }
  return 'pending';
}

export function DeliverablesSection({ projectId }: DeliverablesSectionProps) {
  const firmType = useFirmType();
  const { data: operationalSettings } = useOperationalSettings();
  const overdueHours = operationalSettings?.acceptance_reminder_hours ?? 48;

  const { data: deliverables, isLoading } = useDeliverables(projectId);
  const { data: submissions, isLoading: submissionsLoading } = useSubmissions(projectId);
  const { data: siteVisits } = useSiteVisits(projectId);
  const { data: scopesData } = useProjectScopes(projectId);
  const { data: teamMembers } = useTeamMembers();
  const createDeliverable = useCreateDeliverable();
  const updateDeliverable = useUpdateDeliverable();
  const deleteDeliverable = useDeleteDeliverable();
  const createSubmission = useCreateSubmission();
  const updateSubmission = useUpdateSubmission();
  const deleteSubmission = useDeleteSubmission();

  const [isAdding, setIsAdding] = useState(false);
  const [addingToSubmission, setAddingToSubmission] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newAssignee, setNewAssignee] = useState('');
  const [newReviewer, setNewReviewer] = useState('');
  const [newStartDate, setNewStartDate] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [newScopeId, setNewScopeId] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteSubmissionId, setDeleteSubmissionId] = useState<string | null>(null);
  const [groupByScope, setGroupByScope] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedSubmissions, setExpandedSubmissions] = useState<Set<string>>(new Set());

  // Add submission form state
  const [isAddingSubmission, setIsAddingSubmission] = useState(false);
  const [newSubmissionName, setNewSubmissionName] = useState('');
  const [newSubmissionType, setNewSubmissionType] = useState<SubmissionType>('general');

  const scopes = scopesData?.scopes || [];
  const hasScopes = scopes.length > 0;
  const hasSubmissions = (submissions || []).length > 0;

  const toggleSubmissionExpanded = (id: string) => {
    setExpandedSubmissions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Partition deliverables by submission
  const { submissionDeliverables, ungroupedDeliverables } = useMemo(() => {
    const bySubmission: Record<string, Deliverable[]> = {};
    const ungrouped: Deliverable[] = [];

    for (const d of (deliverables || [])) {
      if (d.submission_id) {
        if (!bySubmission[d.submission_id]) bySubmission[d.submission_id] = [];
        bySubmission[d.submission_id].push(d);
      } else {
        ungrouped.push(d);
      }
    }

    return { submissionDeliverables: bySubmission, ungroupedDeliverables: ungrouped };
  }, [deliverables]);

  // Group deliverables by scope for grouped view
  const groupedDeliverables = useMemo(() => {
    if (!deliverables || !groupByScope) return null;
    const groups: Record<string, { name: string; deliverables: Deliverable[] }> = {};
    const ungrouped: Deliverable[] = [];

    for (const d of deliverables) {
      if (d.scope_id && d.scope_name) {
        if (!groups[d.scope_id]) {
          groups[d.scope_id] = { name: d.scope_name, deliverables: [] };
        }
        groups[d.scope_id].deliverables.push(d);
      } else {
        ungrouped.push(d);
      }
    }

    return { groups, ungrouped };
  }, [deliverables, groupByScope]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;

    await createDeliverable.mutateAsync({
      projectId,
      deliverable: {
        title: newTitle.trim(),
        assigned_to: newAssignee === '__none__' ? undefined : newAssignee.trim() || undefined,
        reviewer: newReviewer === '__none__' ? undefined : newReviewer.trim() || undefined,
        start_date: newStartDate || undefined,
        due_date: newDueDate || undefined,
        scope_id: newScopeId || undefined,
        submission_id: addingToSubmission || undefined,
      },
    });

    setNewTitle('');
    setNewAssignee('');
    setNewReviewer('');
    setNewStartDate('');
    setNewDueDate('');
    setNewScopeId('');
    setIsAdding(false);
    setAddingToSubmission(null);
  };

  const handleCreateSubmission = async () => {
    if (!newSubmissionName.trim()) return;

    await createSubmission.mutateAsync({
      projectId,
      submission: {
        name: newSubmissionName.trim(),
        type: newSubmissionType,
      },
    });

    setNewSubmissionName('');
    setNewSubmissionType('general');
    setIsAddingSubmission(false);
  };

  const handleStatusChange = async (deliverable: Deliverable, newStatus: DeliverableStatus) => {
    await updateDeliverable.mutateAsync({
      id: deliverable.id,
      data: { status: newStatus },
    });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await deleteDeliverable.mutateAsync(deleteId);
    setDeleteId(null);
  };

  const deleteTarget = deliverables?.find(d => d.id === deleteId);

  // Summary counts
  const summary = (deliverables || []).reduce(
    (acc, d) => {
      acc[d.status] = (acc[d.status] || 0) + 1;
      acc.total += 1;
      if (isStale(d)) acc.stale += 1;
      if (isOverdue(d)) acc.overdue += 1;
      return acc;
    },
    { pending: 0, in_progress: 0, in_review: 0, revision: 0, approved: 0, delivered: 0, total: 0, stale: 0, overdue: 0 } as Record<string, number>
  );

  const hasStatusBreakdown = summary.in_progress > 0 || summary.in_review > 0 || summary.revision > 0 || summary.approved > 0 || summary.delivered > 0;

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-4">Loading deliverables...</div>;
  }

  return (
    <div className="space-y-3">
      {/* Empty state — primary CTA is "Add submission package" (parent-first natural path);
          standalone deliverable is offered as a secondary text link.
          See #96: drop dual CTA and nudge users to create a parent first. */}
      {summary.total === 0 && !isAdding && !hasSubmissions && !isAddingSubmission && (
        <div
          className="border border-dashed rounded-lg bg-muted/20 flex flex-col items-center justify-center py-12 text-center"
          role="status"
          data-testid="deliverables-empty-state"
        >
          <ClipboardList className="h-10 w-10 text-muted-foreground/40 mb-3" />
          <h3 className="text-base font-medium text-foreground mb-1">No deliverables yet</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-xs">
            {firmType === 'architect'
              ? 'Group drawings, specs, and review items under a submission package (e.g., Development Permit).'
              : firmType === 'general_contractor'
              ? "Group what needs to get done under a submission package (e.g., a permit) so the ball stays together."
              : 'Group deliverables under a submission package (e.g., Development Permit) so they move through review together.'}
          </p>
          <div className="flex flex-col items-center gap-2">
            <Button
              onClick={() => setIsAddingSubmission(true)}
              data-testid="empty-state-add-submission-package"
              title="Group deliverables under a package (e.g., Development Permit)"
            >
              <FolderPlus className="h-4 w-4 mr-1" />
              Add submission package
            </Button>
            <button
              type="button"
              onClick={() => { setAddingToSubmission(null); setIsAdding(true); }}
              className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              data-testid="empty-state-add-standalone-deliverable"
            >
              or add a standalone deliverable
            </button>
          </div>
        </div>
      )}

      {/* Summary bar */}
      {summary.total > 0 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground pb-1 flex-wrap">
          <span className="font-medium">{summary.total} deliverable{summary.total !== 1 ? 's' : ''}</span>
          {hasStatusBreakdown && <span className="text-gray-300">|</span>}
          {summary.pending > 0 && <span>{summary.pending} pending</span>}
          {summary.in_progress > 0 && <span className="text-blue-600">{summary.in_progress} in progress</span>}
          {summary.in_review > 0 && <span className="text-yellow-600">{summary.in_review} in review</span>}
          {summary.revision > 0 && <span className="text-orange-600">{summary.revision} revision</span>}
          {summary.approved > 0 && <span className="text-green-600">{summary.approved} approved</span>}
          {summary.delivered > 0 && <span className="text-emerald-700">{summary.delivered} delivered</span>}
          {summary.stale > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-red-600 font-medium flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {summary.stale} stale
              </span>
            </>
          )}
          {summary.overdue > 0 && !summary.stale && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-red-600 font-medium">{summary.overdue} overdue</span>
            </>
          )}
          {summary.overdue > 0 && summary.stale > 0 && (
            <span className="text-red-600 font-medium">{summary.overdue} overdue</span>
          )}
          {/* Group by scope toggle - only show when scopes exist and not using submissions */}
          {hasScopes && !hasSubmissions && (
            <>
              <span className="text-gray-300 ml-auto">|</span>
              <button
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${
                  groupByScope
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'hover:text-foreground'
                }`}
                onClick={() => setGroupByScope(!groupByScope)}
                title={groupByScope ? 'Show flat list' : 'Group by discipline'}
              >
                {groupByScope ? <LayoutGrid className="h-3 w-3" /> : <List className="h-3 w-3" />}
                {groupByScope ? 'Grouped' : 'Group'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Submission groups (when submissions exist) */}
      {hasSubmissions && (
        <div className="space-y-2">
          {(submissions || []).map((sub) => (
            <SubmissionGroup
              key={sub.id}
              submission={sub}
              deliverables={submissionDeliverables[sub.id] || []}
              siteVisits={(siteVisits || []).filter((v) => v.submission_id === sub.id)}
              isExpanded={expandedSubmissions.has(sub.id)}
              onToggle={() => toggleSubmissionExpanded(sub.id)}
              onUpdate={(id, data) => updateSubmission.mutateAsync({ id, data })}
              onDelete={(id) => setDeleteSubmissionId(id)}
              onAddDeliverable={(submissionId) => {
                setAddingToSubmission(submissionId);
                setIsAdding(true);
              }}
              renderDeliverables={(dels) => (
                <div className="space-y-1 py-1">
                  {dels.map((deliverable) => (
                    <DeliverableRow
                      key={deliverable.id}
                      deliverable={deliverable}
                      allStatuses={ALL_STATUSES}
                      statusLabels={STATUS_LABELS}
                      showScope={hasScopes}
                      onStatusChange={handleStatusChange}
                      onDelete={setDeleteId}
                      expandedId={expandedId}
                      onToggleExpand={setExpandedId}
                      teamMembers={teamMembers}
                      scopes={scopes}
                      onUpdate={(id, data) => updateDeliverable.mutateAsync({ id, data })}
                      overdueHours={overdueHours}
                    />
                  ))}
                </div>
              )}
            />
          ))}

          {/* Ungrouped deliverables */}
          {ungroupedDeliverables.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-1.5 opacity-60">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Ungrouped
                </span>
                <div className="flex-1 border-t border-dashed" />
              </div>
              <div className="space-y-1">
                {ungroupedDeliverables.map((deliverable) => (
                  <DeliverableRow
                    key={deliverable.id}
                    deliverable={deliverable}
                    allStatuses={ALL_STATUSES}
                    statusLabels={STATUS_LABELS}
                    showScope={hasScopes}
                    onStatusChange={handleStatusChange}
                    onDelete={setDeleteId}
                    expandedId={expandedId}
                    onToggleExpand={setExpandedId}
                    teamMembers={teamMembers}
                    scopes={scopes}
                    onUpdate={(id, data) => updateDeliverable.mutateAsync({ id, data })}
                    overdueHours={overdueHours}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Deliverables list (flat view when no submissions) */}
      {!hasSubmissions && (
        groupByScope && groupedDeliverables ? (
          <div className="space-y-4">
            {Object.entries(groupedDeliverables.groups).map(([scopeId, group]) => (
              <ScopeGroup
                key={scopeId}
                name={group.name}
                deliverables={group.deliverables}
                allStatuses={ALL_STATUSES}
                statusLabels={STATUS_LABELS}
                onStatusChange={handleStatusChange}
                onDelete={setDeleteId}
                expandedId={expandedId}
                onToggleExpand={setExpandedId}
                teamMembers={teamMembers}
                scopes={scopes}
                onUpdate={(id, data) => updateDeliverable.mutateAsync({ id, data })}
                overdueHours={overdueHours}
              />
            ))}
            {groupedDeliverables.ungrouped.length > 0 && (
              <ScopeGroup
                name="Unassigned"
                deliverables={groupedDeliverables.ungrouped}
                allStatuses={ALL_STATUSES}
                statusLabels={STATUS_LABELS}
                onStatusChange={handleStatusChange}
                onDelete={setDeleteId}
                muted
                expandedId={expandedId}
                onToggleExpand={setExpandedId}
                teamMembers={teamMembers}
                scopes={scopes}
                onUpdate={(id, data) => updateDeliverable.mutateAsync({ id, data })}
                overdueHours={overdueHours}
              />
            )}
          </div>
        ) : (
          <div className="space-y-1">
            {(deliverables || []).map((deliverable) => (
              <DeliverableRow
                key={deliverable.id}
                deliverable={deliverable}
                allStatuses={ALL_STATUSES}
                statusLabels={STATUS_LABELS}
                showScope={!groupByScope && hasScopes}
                onStatusChange={handleStatusChange}
                onDelete={setDeleteId}
                expandedId={expandedId}
                onToggleExpand={setExpandedId}
                teamMembers={teamMembers}
                scopes={scopes}
                onUpdate={(id, data) => updateDeliverable.mutateAsync({ id, data })}
                overdueHours={overdueHours}
              />
            ))}
          </div>
        )
      )}

      {/* Add submission form */}
      {isAddingSubmission && (
        <div className="space-y-2 py-3 px-3 rounded-md border border-dashed border-blue-300 bg-blue-50/30">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Submission package name (e.g., Development Permit)..."
              value={newSubmissionName}
              onChange={(e) => setNewSubmissionName(e.target.value)}
              className="flex-1 h-8 text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateSubmission();
                if (e.key === 'Escape') setIsAddingSubmission(false);
              }}
            />
            <Select value={newSubmissionType} onValueChange={(v) => setNewSubmissionType(v as SubmissionType)}>
              <SelectTrigger className="w-[140px] h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="permit">Permit</SelectItem>
                <SelectItem value="client_submission">Client</SelectItem>
                <SelectItem value="internal">Internal</SelectItem>
                <SelectItem value="engineering">Engineering</SelectItem>
                <SelectItem value="general">General</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="default" className="h-8" onClick={handleCreateSubmission} disabled={!newSubmissionName.trim() || createSubmission.isPending}>
              {createSubmission.isPending ? 'Adding...' : 'Add'}
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setIsAddingSubmission(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Add new deliverable */}
      {isAdding ? (
        <div className="space-y-2 py-3 px-3 rounded-md border border-dashed border-primary/30 bg-primary/5" data-testid="add-deliverable-form">
          {/* Parent (submission package) picker — primary path nudge.
              Shown as the first field when opened from the section-level CTA
              and submissions exist. When opened from inside a SubmissionGroup,
              the parent is preselected and shown as a read-only chip. */}
          {hasSubmissions && (
            addingToSubmission ? (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">Adding to:</span>
                <span
                  className="font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200"
                  data-testid="add-deliverable-parent-chip"
                >
                  {(submissions || []).find((s) => s.id === addingToSubmission)?.name || 'submission package'}
                </span>
                <button
                  type="button"
                  className="text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  onClick={() => setAddingToSubmission(null)}
                  data-testid="add-deliverable-change-parent"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted-foreground whitespace-nowrap">
                  Submission package:
                </label>
                <Select
                  value={addingToSubmission ?? '__standalone__'}
                  onValueChange={(v) => setAddingToSubmission(v === '__standalone__' ? null : v)}
                >
                  <SelectTrigger
                    className="flex-1 h-8 text-sm"
                    data-testid="add-deliverable-parent-select"
                  >
                    <SelectValue placeholder="Pick a submission package..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(submissions || []).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="__standalone__">Standalone (rare)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )
          )}
          <div className="flex items-center gap-2">
            <Input
              placeholder="Deliverable title..."
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="flex-1 h-8 text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') setIsAdding(false);
              }}
            />
            <Button size="sm" variant="default" className="h-8" onClick={handleCreate} disabled={!newTitle.trim() || createDeliverable.isPending}>
              {createDeliverable.isPending ? 'Adding...' : 'Add'}
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => { setIsAdding(false); setAddingToSubmission(null); }}>
              Cancel
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Select value={newAssignee} onValueChange={setNewAssignee}>
              <SelectTrigger className="flex-1 h-8 text-sm">
                <SelectValue placeholder="Assigned to..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {teamMembers?.map((m) => (
                  <SelectItem key={m.uid} value={m.name || m.email}>
                    {m.name || m.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={newReviewer} onValueChange={setNewReviewer}>
              <SelectTrigger className="flex-1 h-8 text-sm">
                <SelectValue placeholder="Reviewer..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {teamMembers?.map((m) => (
                  <SelectItem key={m.uid} value={m.name || m.email}>
                    {m.name || m.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={newStartDate}
              onChange={(e) => setNewStartDate(e.target.value)}
              className="w-[140px] h-8 text-sm"
              placeholder="Start date"
              title="Start date"
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            />
            <Input
              type="date"
              value={newDueDate}
              onChange={(e) => setNewDueDate(e.target.value)}
              className="w-[140px] h-8 text-sm"
              placeholder="Due date"
              title="Due date"
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            />
            {hasScopes && (
              <Select value={newScopeId} onValueChange={setNewScopeId}>
                <SelectTrigger className="w-[160px] h-8 text-sm">
                  <SelectValue placeholder="Discipline..." />
                </SelectTrigger>
                <SelectContent>
                  {scopes.map((scope) => (
                    <SelectItem key={scope.id} value={scope.id}>
                      {scope.scope_type_name || 'Unknown'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      ) : (
        summary.total > 0 && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => { setAddingToSubmission(null); setIsAdding(true); }}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add deliverable
            </Button>
            {!isAddingSubmission && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => setIsAddingSubmission(true)}
                title="Group deliverables under a package (e.g., Development Permit)"
              >
                <FolderPlus className="h-4 w-4 mr-1" />
                Add submission package
              </Button>
            )}
          </div>
        )
      )}

      {/* Delete deliverable confirmation dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete deliverable?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? <>This will permanently remove <span className="font-medium text-foreground">"{deleteTarget.title}"</span>. This action cannot be undone.</>
                : 'This will permanently remove this deliverable. This action cannot be undone.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete submission confirmation dialog */}
      <AlertDialog open={!!deleteSubmissionId} onOpenChange={(open) => !open && setDeleteSubmissionId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete submission?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the submission group. Deliverables inside it will be moved to "Ungrouped" — they won't be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteSubmissionId) {
                  await deleteSubmission.mutateAsync(deleteSubmissionId);
                  setDeleteSubmissionId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// --- Linked site visits panel (shown in expanded deliverable detail) ---

function LinkedSiteVisitsPanel({ projectId, deliverableId }: { projectId: string; deliverableId: string }) {
  const { data: allVisits } = useSiteVisits(projectId);
  const linked = (allVisits || []).filter((v: SiteVisit) => v.deliverable_id === deliverableId);

  if (linked.length === 0) return null;

  return (
    <div className="px-3 py-2 border-t bg-muted/10">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
        <Camera className="h-3 w-3" />
        Linked site visit{linked.length !== 1 ? 's' : ''} ({linked.length})
      </p>
      <ul className="space-y-1">
        {linked.map((v: SiteVisit) => (
          <li key={v.id} className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className={`px-1.5 py-0.5 rounded text-[10px] border ${
              v.status === 'submitted' ? 'bg-green-50 text-green-700 border-green-200' :
              v.status === 'completed' ? 'bg-blue-50 text-blue-700 border-blue-200' :
              'bg-gray-50 text-gray-600 border-gray-200'
            }`}>
              {v.status}
            </span>
            <span className="font-medium text-foreground truncate">{v.title}</span>
            <span className="text-muted-foreground whitespace-nowrap">
              {new Date(v.visit_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Portal prompt banner (shown in expanded panel when scope is external) ---

function PortalPromptBanner({
  deliverable,
  scope,
}: {
  deliverable: Deliverable;
  scope: ProjectScope;
}) {
  const [confirming, setConfirming] = useState(false);
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();

  const { data: shareData } = useQuery({
    queryKey: ['project-share', deliverable.project_id],
    queryFn: () => api.projectShare.getForProject(deliverable.project_id),
    staleTime: 30_000,
  });

  // Wait for share data to load
  if (!shareData) return null;

  const orgName = scope.organization_name ?? scope.scope_type_name ?? 'this organization';
  const contactEmails = (scope.contacts ?? [])
    .map(c => c.contact_email)
    .filter((e): e is string => !!e);
  const portalExists = shareData.view !== null;

  if (portalExists) {
    return (
      <div className="mx-3 mb-2 flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
        <Globe className="w-3 h-3 shrink-0 text-slate-400" />
        <span>{orgName} has an active portal.</span>
      </div>
    );
  }

  const handleCreate = async () => {
    if (contactEmails.length === 0) return;
    setCreating(true);
    try {
      await api.projectShare.share(deliverable.project_id, { emails: contactEmails });
      queryClient.invalidateQueries({ queryKey: ['project-share', deliverable.project_id] });
      toast.success(`Portal created for ${orgName}`);
      setConfirming(false);
    } catch {
      toast.error('Failed to create portal');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="mx-3 mb-2 bg-blue-50 border border-blue-200 rounded px-3 py-2 text-xs space-y-2" data-testid="portal-prompt-banner">
      <div className="flex items-center gap-2 text-blue-800">
        <Globe className="w-3 h-3 shrink-0" />
        <span>This deliverable is assigned to <strong>{orgName}</strong>. Create a portal for them?</span>
        {!confirming && (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto h-6 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
            onClick={() => setConfirming(true)}
          >
            Set Up Portal
          </Button>
        )}
      </div>
      {confirming && (
        <div className="space-y-1.5 pt-1 border-t border-blue-200">
          <p className="text-blue-700"><span className="font-medium">Portal name:</span> {orgName} Portal</p>
          {contactEmails.length > 0 ? (
            <p className="text-blue-700"><span className="font-medium">Members:</span> {contactEmails.join(', ')}</p>
          ) : (
            <p className="text-amber-700">No contact emails on file for {orgName}. Add contacts to pre-fill members.</p>
          )}
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              className="h-6 text-xs"
              onClick={handleCreate}
              disabled={creating || contactEmails.length === 0}
            >
              {creating ? 'Creating…' : 'Create Portal'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Extracted sub-components ---

function DeliverableRow({
  deliverable,
  allStatuses,
  statusLabels,
  showScope,
  onStatusChange,
  onDelete,
  expandedId,
  onToggleExpand,
  teamMembers,
  scopes,
  onUpdate,
  overdueHours,
}: {
  deliverable: Deliverable;
  allStatuses: DeliverableStatus[];
  statusLabels: Record<DeliverableStatus, string>;
  showScope?: boolean;
  onStatusChange: (d: Deliverable, status: DeliverableStatus) => void;
  onDelete: (id: string) => void;
  expandedId?: string | null;
  onToggleExpand?: (id: string | null) => void;
  teamMembers?: TeamMember[];
  scopes?: ProjectScope[];
  onUpdate?: (id: string, data: DeliverableUpdate) => Promise<any>;
  overdueHours?: number;
}) {
  const resolvedOverdueHours = overdueHours ?? 48;
  const stale = isStale(deliverable);
  const overdue = isOverdue(deliverable);
  const ballHolder = getBallHolder(deliverable);
  const isExpanded = expandedId === deliverable.id;

  const docCount = (deliverable as any).document_count || 0;
  const attachmentCount = docCount; // file count not available from API; doc count only

  const handleRowClick = () => {
    onToggleExpand?.(isExpanded ? null : deliverable.id);
  };

  return (
    <div className={`rounded-md border overflow-hidden ${
      stale
        ? 'border-red-300'
        : overdue
          ? 'border-amber-200'
          : ''
    }`}>
      <div
        className={`flex items-center gap-2 py-2 px-3 transition-colors group cursor-pointer ${
          stale
            ? 'bg-red-50/50 hover:bg-red-50'
            : overdue
              ? 'bg-amber-50/30 hover:bg-amber-50/50'
              : 'bg-card hover:bg-accent/50'
        }`}
        onClick={handleRowClick}
      >
        {/* Expand chevron */}
        {isExpanded
          ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        }

        {/* Status selector */}
        <Select
          value={deliverable.status}
          onValueChange={(value) => onStatusChange(deliverable, value as DeliverableStatus)}
        >
          <SelectTrigger
            className="w-[130px] h-7 text-xs border-0 p-0 focus:ring-0"
            onClick={(e) => e.stopPropagation()}
          >
            <DeliverableStatusBadge status={deliverable.status} />
          </SelectTrigger>
          <SelectContent>
            {allStatuses.map((s) => (
              <SelectItem key={s} value={s}>
                <DeliverableStatusBadge status={s} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Title */}
        <span className="flex-1 text-sm font-medium truncate">{deliverable.title}</span>

        {/* Billable item link indicator */}
        {deliverable.billable_item_id && (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-200 whitespace-nowrap"
                title="Linked to billable item">
            $
          </span>
        )}

        {/* Scope badge (flat view only) */}
        {showScope && deliverable.scope_name && (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 border border-violet-200 whitespace-nowrap">
            {deliverable.scope_name}
          </span>
        )}

        {/* Time in status */}
        {deliverable.status_changed_at && deliverable.status !== 'pending' && (
          <span
            className={`flex items-center gap-1 text-xs whitespace-nowrap ${
              stale ? 'text-red-600 font-medium' : 'text-muted-foreground'
            }`}
            title={`${statusLabels[deliverable.status]} since ${new Date(deliverable.status_changed_at).toLocaleDateString()}`}
          >
            {stale && <AlertTriangle className="h-3 w-3" />}
            <Clock className="h-3 w-3" />
            {timeAgo(deliverable.status_changed_at)}
          </span>
        )}

        {/* Ball is with indicator */}
        {ballHolder && (
          <span
            className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200 whitespace-nowrap"
            title={`${ballHolder.role}`}
          >
            <UserCheck className="h-3 w-3" />
            {ballHolder.name}
          </span>
        )}

        {/* Assignee + Reviewer shown when no ball holder (pending status) */}
        {!ballHolder && deliverable.assigned_to && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
            <User className="h-3 w-3" />
            {deliverable.assigned_to}
          </span>
        )}

        {/* Reviewer shown subtly when there's a ball holder but reviewer is different */}
        {ballHolder && deliverable.reviewer && ballHolder.name !== deliverable.reviewer && deliverable.status !== 'in_review' && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground/60 whitespace-nowrap" title="Reviewer">
            <Eye className="h-3 w-3" />
            {deliverable.reviewer}
          </span>
        )}

        {/* Due date with overdue indicator */}
        {deliverable.due_date && (
          <span className={`flex items-center gap-1 text-xs whitespace-nowrap ${
            overdue ? 'text-red-600 font-medium' : 'text-muted-foreground'
          }`}>
            <Calendar className="h-3 w-3" />
            {new Date(deliverable.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            {overdue && <span className="text-[10px] uppercase tracking-wide">overdue</span>}
          </span>
        )}

        {/* Attachment count badge */}
        {attachmentCount > 0 && (
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground px-1.5 py-0.5 rounded bg-muted/50 whitespace-nowrap" title={`${attachmentCount} linked document${attachmentCount !== 1 ? 's' : ''}`}>
            <Paperclip className="h-3 w-3" />
            {attachmentCount}
          </span>
        )}

        {/* Acceptance status badge (Phase 10e — only shown after V118 migration) */}
        {(() => {
          const acceptStatus = getDeliverableAcceptanceStatus(deliverable, resolvedOverdueHours);
          if (acceptStatus === 'accepted') {
            return (
              <span
                className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 whitespace-nowrap"
                title={`Accepted by ${deliverable.accepted_by_email ?? 'portal user'}`}
              >
                <ThumbsUp className="h-3 w-3" />
                Accepted
              </span>
            );
          }
          if (acceptStatus === 'overdue') {
            return (
              <span
                className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 whitespace-nowrap"
                title={`Not accepted — over ${resolvedOverdueHours} hours`}
              >
                <AlertTriangle className="h-3 w-3" />
                Acceptance Overdue
              </span>
            );
          }
          if (acceptStatus === 'pending') {
            return (
              <span
                className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 whitespace-nowrap"
                title="Awaiting acceptance from portal user"
              >
                <Hourglass className="h-3 w-3" />
                Pending Acceptance
              </span>
            );
          }
          return null;
        })()}

        {/* Delete */}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          onClick={(e) => { e.stopPropagation(); onDelete(deliverable.id); }}
        >
          <Trash2 className="h-3 w-3 text-destructive" />
        </Button>
      </div>

      {/* Expanded panel: edit fields + attachments + linked site visits */}
      {isExpanded && (
        <>
          {onUpdate && (
            <DeliverableEditFields
              deliverable={deliverable}
              teamMembers={teamMembers}
              scopes={scopes}
              onUpdate={onUpdate}
            />
          )}
          {/* Portal prompt: show when deliverable is assigned to an external scope */}
          {(() => {
            const scopeDetails = deliverable.scope_id
              ? scopes?.find(s => s.id === deliverable.scope_id) ?? null
              : null;
            return scopeDetails && scopeDetails.scope_type_slug !== 'self' && scopeDetails.scope_type_slug
              ? <PortalPromptBanner deliverable={deliverable} scope={scopeDetails} />
              : null;
          })()}
          <DeliverableFileList deliverableId={deliverable.id} />
          <DeliverableDocumentList deliverableId={deliverable.id} />
          <LinkedSiteVisitsPanel projectId={deliverable.project_id} deliverableId={deliverable.id} />
        </>
      )}
    </div>
  );
}

export function DeliverableEditFields({
  deliverable,
  teamMembers,
  scopes,
  onUpdate,
}: {
  deliverable: Deliverable;
  teamMembers?: TeamMember[];
  scopes?: ProjectScope[];
  onUpdate: (id: string, data: DeliverableUpdate) => Promise<any>;
}) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [initialTitleValue, setInitialTitleValue] = useState('');

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState('');
  const [initialDescriptionValue, setInitialDescriptionValue] = useState('');

  const [dateError, setDateError] = useState<string | null>(null);

  const hasScopes = scopes && scopes.length > 0;

  return (
    <div className="px-3 py-2 border-t bg-muted/20 space-y-2">
      {/* Title - click to edit */}
      <div>
        {isEditingTitle ? (
          <div>
            <Input
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={() => {
                if (titleValue !== initialTitleValue && titleValue.trim()) {
                  onUpdate(deliverable.id, { title: titleValue.trim() });
                }
                queueMicrotask(() => setIsEditingTitle(false));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                } else if (e.key === 'Escape') {
                  setTitleValue(initialTitleValue);
                  setIsEditingTitle(false);
                }
              }}
              autoFocus
              className="text-sm font-medium"
              data-testid="deliverable-title-input"
            />
            <InlineEditActions
              onCancel={() => {
                setTitleValue(initialTitleValue);
                setIsEditingTitle(false);
              }}
            />
          </div>
        ) : (
          <div className="relative inline-block group">
            <p
              className="text-sm font-medium cursor-pointer hover:ring-1 hover:ring-gray-400 rounded px-2 py-1 pr-8 transition-all duration-200"
              role="button"
              tabIndex={0}
              onClick={() => {
                setTitleValue(deliverable.title);
                setInitialTitleValue(deliverable.title);
                setIsEditingTitle(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setTitleValue(deliverable.title);
                  setInitialTitleValue(deliverable.title);
                  setIsEditingTitle(true);
                }
              }}
              title="Click to edit title"
              data-testid="deliverable-title-display"
            >
              {deliverable.title}
            </p>
            <Pencil
              aria-hidden
              className="absolute top-1/2 -translate-y-1/2 right-2 h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600 transition-colors"
            />
          </div>
        )}
      </div>

      {/* Description - click to edit */}
      <div>
        {isEditingDescription ? (
          <div>
            <Textarea
              value={descriptionValue}
              onChange={(e) => setDescriptionValue(e.target.value)}
              onBlur={() => {
                if (descriptionValue !== initialDescriptionValue) {
                  onUpdate(deliverable.id, { description: descriptionValue || undefined });
                }
                queueMicrotask(() => setIsEditingDescription(false));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setDescriptionValue(initialDescriptionValue);
                  setIsEditingDescription(false);
                }
              }}
              autoFocus
              className="text-sm min-h-[60px]"
              placeholder="Add a description..."
              data-testid="deliverable-description-input"
            />
            <InlineEditActions
              onCancel={() => {
                setDescriptionValue(initialDescriptionValue);
                setIsEditingDescription(false);
              }}
              showEnterHint={false}
            />
          </div>
        ) : (
          <div className="relative inline-block group">
            <p
              className={`text-sm ${deliverable.description ? 'whitespace-pre-wrap' : 'text-muted-foreground italic'} cursor-pointer hover:ring-1 hover:ring-gray-400 rounded px-2 py-1 pr-8 transition-all duration-200`}
              role="button"
              tabIndex={0}
              onClick={() => {
                const current = deliverable.description || '';
                setDescriptionValue(current);
                setInitialDescriptionValue(current);
                setIsEditingDescription(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  const current = deliverable.description || '';
                  setDescriptionValue(current);
                  setInitialDescriptionValue(current);
                  setIsEditingDescription(true);
                }
              }}
              title="Click to edit description"
              data-testid="deliverable-description-display"
            >
              {deliverable.description || 'Add description...'}
            </p>
            <Pencil
              aria-hidden
              className="absolute top-1/2 -translate-y-1/2 right-2 h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600 transition-colors"
            />
          </div>
        )}
      </div>

      {/* Compact fields: 2-column grid */}
      <div className="grid grid-cols-2 gap-2">
        {/* Assigned to */}
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Assigned to</label>
          <Select
            value={deliverable.assigned_to || '__none__'}
            onValueChange={(value) => {
              onUpdate(deliverable.id, {
                assigned_to: value === '__none__' ? null : value,
              });
            }}
          >
            <SelectTrigger className="h-8 text-sm" data-testid="deliverable-assignee-select" onClick={(e) => e.stopPropagation()}>
              <SelectValue placeholder="Unassigned" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Unassigned</SelectItem>
              {teamMembers?.map((m) => (
                <SelectItem key={m.uid} value={m.name || m.email}>
                  {m.name || m.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Reviewer */}
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Reviewer</label>
          <Select
            value={deliverable.reviewer || '__none__'}
            onValueChange={(value) => {
              onUpdate(deliverable.id, {
                reviewer: value === '__none__' ? null : value,
              });
            }}
          >
            <SelectTrigger className="h-8 text-sm" data-testid="deliverable-reviewer-select" onClick={(e) => e.stopPropagation()}>
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {teamMembers?.map((m) => (
                <SelectItem key={m.uid} value={m.name || m.email}>
                  {m.name || m.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Start date */}
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Start date</label>
          <Input
            type="date"
            value={deliverable.start_date || ''}
            onChange={(e) => {
              const newStart = e.target.value || null;
              const due = deliverable.due_date || null;
              if (newStart && due && newStart > due) {
                setDateError('Start date must not be after due date');
                return;
              }
              setDateError(null);
              onUpdate(deliverable.id, { start_date: newStart });
            }}
            className={`h-8 text-sm ${dateError ? 'border-red-500' : ''}`}
            data-testid="deliverable-start-date-input"
          />
        </div>

        {/* Due date */}
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Due date</label>
          <Input
            type="date"
            value={deliverable.due_date || ''}
            onChange={(e) => {
              const newDue = e.target.value || null;
              const start = deliverable.start_date || null;
              if (start && newDue && start > newDue) {
                setDateError('Start date must not be after due date');
                return;
              }
              setDateError(null);
              onUpdate(deliverable.id, { due_date: newDue });
            }}
            className={`h-8 text-sm ${dateError ? 'border-red-500' : ''}`}
            data-testid="deliverable-due-date-input"
          />
        </div>

        {/* Date validation error - spans both columns */}
        {dateError && (
          <p className="col-span-2 text-xs text-red-600 -mt-1" data-testid="deliverable-date-error">
            {dateError}
          </p>
        )}

        {/* Scope */}
        {hasScopes && (
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Scope</label>
            <Select
              value={deliverable.scope_id || '__none__'}
              onValueChange={(value) => {
                onUpdate(deliverable.id, {
                  scope_id: value === '__none__' ? null : value,
                });
              }}
            >
              <SelectTrigger className="h-8 text-sm" data-testid="deliverable-scope-select" onClick={(e) => e.stopPropagation()}>
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {scopes?.map((scope) => (
                  <SelectItem key={scope.id} value={scope.id}>
                    {scope.scope_type_name || 'Unknown'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Portal visibility toggle */}
      <div className="flex items-center justify-between pt-1 border-t">
        <div className="flex items-center gap-1.5">
          <Globe className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Visible in client portal</span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={!!deliverable.portal_visible}
          data-testid="deliverable-portal-visible-toggle"
          onClick={(e) => {
            e.stopPropagation();
            onUpdate(deliverable.id, { portal_visible: !deliverable.portal_visible });
          }}
          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
            deliverable.portal_visible ? 'bg-primary' : 'bg-input'
          }`}
          title={deliverable.portal_visible ? 'Hide from client portal' : 'Share in client portal'}
        >
          <span
            className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform ${
              deliverable.portal_visible ? 'translate-x-4' : 'translate-x-0'
            }`}
          />
        </button>
      </div>
    </div>
  );
}

function ScopeGroup({
  name,
  deliverables,
  allStatuses,
  statusLabels,
  onStatusChange,
  onDelete,
  muted,
  expandedId,
  onToggleExpand,
  teamMembers,
  scopes,
  onUpdate,
  overdueHours,
}: {
  name: string;
  deliverables: Deliverable[];
  allStatuses: DeliverableStatus[];
  statusLabels: Record<DeliverableStatus, string>;
  onStatusChange: (d: Deliverable, status: DeliverableStatus) => void;
  onDelete: (id: string) => void;
  muted?: boolean;
  expandedId?: string | null;
  onToggleExpand?: (id: string | null) => void;
  teamMembers?: TeamMember[];
  scopes?: ProjectScope[];
  onUpdate?: (id: string, data: DeliverableUpdate) => Promise<any>;
  overdueHours?: number;
}) {
  // Build mini summary for group header
  const counts: Record<string, number> = {};
  for (const d of deliverables) {
    counts[d.status] = (counts[d.status] || 0) + 1;
  }

  const summaryParts: string[] = [];
  if (counts.in_progress) summaryParts.push(`${counts.in_progress} in progress`);
  if (counts.in_review) summaryParts.push(`${counts.in_review} in review`);
  if (counts.revision) summaryParts.push(`${counts.revision} revision`);
  if (counts.pending) summaryParts.push(`${counts.pending} pending`);
  if (counts.approved) summaryParts.push(`${counts.approved} approved`);
  if (counts.delivered) summaryParts.push(`${counts.delivered} delivered`);

  return (
    <div>
      <div className={`flex items-center gap-2 mb-1.5 ${muted ? 'opacity-60' : ''}`}>
        <span className="text-xs font-semibold uppercase tracking-wider text-violet-600">
          {name}
        </span>
        <span className="text-[10px] text-muted-foreground">
          {summaryParts.join(' · ')}
        </span>
        <div className="flex-1 border-t border-dashed border-violet-200" />
      </div>
      <div className="space-y-1">
        {deliverables.map((d) => (
          <DeliverableRow
            key={d.id}
            deliverable={d}
            allStatuses={allStatuses}
            statusLabels={statusLabels}
            onStatusChange={onStatusChange}
            onDelete={onDelete}
            expandedId={expandedId}
            onToggleExpand={onToggleExpand}
            teamMembers={teamMembers}
            scopes={scopes}
            onUpdate={onUpdate}
            overdueHours={overdueHours}
          />
        ))}
      </div>
    </div>
  );
}
